import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import Message from './Message';
import EmojiPicker from 'emoji-picker-react';
import '../styles/Chat.css';

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  `${window.location.protocol}//${window.location.hostname}:3001`;
const socket = io(SOCKET_URL);
const USERNAME_STORAGE_KEY = 'rtc_username';
const JOIN_ANNOUNCED_KEY = 'rtc_join_announced';
const MAX_USERNAME_LENGTH = 24;

const normalizeName = (value = '') =>
  value.trim().replace(/\s+/g, ' ').slice(0, MAX_USERNAME_LENGTH);

const createMessageId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

function Chat() {
  const [username, setUsername] = useState('');
  const [pendingName, setPendingName] = useState('');
  const [hasJoined, setHasJoined] = useState(false);
  const [message, setMessage] = useState('');
  const [chat, setChat] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const usernameRef = useRef('');
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    const storedName = normalizeName(window.sessionStorage.getItem(USERNAME_STORAGE_KEY) || '');
    if (!storedName) {
      setHasJoined(false);
      return;
    }

    setUsername(storedName);
    usernameRef.current = storedName;
    setHasJoined(true);

    if (window.sessionStorage.getItem(JOIN_ANNOUNCED_KEY) !== storedName) {
      socket.emit('user-joined', storedName);
      window.sessionStorage.setItem(JOIN_ANNOUNCED_KEY, storedName);
    }
  }, []);

  useEffect(() => {
    const clearJoinFlag = () => {
      window.sessionStorage.removeItem(JOIN_ANNOUNCED_KEY);
    };

    window.addEventListener('beforeunload', clearJoinFlag);

    return () => {
      window.removeEventListener('beforeunload', clearJoinFlag);
    };
  }, []);

  useEffect(() => {
    const handleChatMessage = (data) => {
      setChat(prev => [...prev, { ...data, id: data.id || createMessageId() }]);
    };

    const handleSystemMessage = (data) => {
      setChat(prev => [...prev, { ...data, system: true, id: createMessageId() }]);
    };

    const handleTypingEvent = (user) => {
      if (user && user !== usernameRef.current) {
        setIsTyping(true);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1500);
      }
    };

    socket.on('chat-message', handleChatMessage);
    socket.on('system-message', handleSystemMessage);
    socket.on('typing', handleTypingEvent);

    return () => {
      socket.off('chat-message', handleChatMessage);
      socket.off('system-message', handleSystemMessage);
      socket.off('typing', handleTypingEvent);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  const joinChat = (e) => {
    e.preventDefault();
    const safeName = normalizeName(pendingName);
    if (!safeName) {
      return;
    }

    setUsername(safeName);
    usernameRef.current = safeName;
    setHasJoined(true);
    window.sessionStorage.setItem(USERNAME_STORAGE_KEY, safeName);

    socket.emit('user-joined', safeName);
    window.sessionStorage.setItem(JOIN_ANNOUNCED_KEY, safeName);
  };

  const sendMessage = (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();

    if (trimmedMessage && usernameRef.current) {
      const msgObj = {
        id: createMessageId(),
        user: usernameRef.current,
        text: trimmedMessage,
        self: true,
      };
      socket.emit('chat-message', { ...msgObj, self: false });
      setChat(prev => [...prev, msgObj]);
      setMessage('');
      setShowPicker(false);
    }
  };

  const handleTyping = () => {
    if (usernameRef.current) {
      socket.emit('typing', usernameRef.current);
    }
  };

  if (!hasJoined) {
    return (
      <div className="chat-container join-panel">
        <div className="join-card">
          <h3>Join the conversation</h3>
          <p>Enter your name to start chatting.</p>
          <form className="join-form" onSubmit={joinChat}>
            <input
              type="text"
              value={pendingName}
              maxLength={MAX_USERNAME_LENGTH}
              onChange={(e) => setPendingName(e.target.value)}
              placeholder="Your name"
              autoFocus
            />
            <button type="submit" className="join-btn">Enter Chat</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-topbar">
        <span className="status-dot" />
        <div className="chat-meta">
          <span className="chat-user">{username}</span>
          <span className="chat-status">Connected</span>
        </div>
      </div>

      <div className="chat-box">
        {chat.length === 0 && (
          <div className="chat-empty">No messages yet. Start the conversation.</div>
        )}
        {chat.map((msg, index) => (
          <Message key={msg.id || index} message={msg} />
        ))}
      </div>

      {isTyping && <div className="typing-indicator">Someone is typing...</div>}

      {showPicker && (
        <div className="emoji-panel">
          <EmojiPicker
            onEmojiClick={(emojiData) => setMessage(prev => `${prev}${emojiData.emoji}`)}
            height={350}
          />
        </div>
      )}

      <form className="chat-form" onSubmit={sendMessage}>
        <button type="button" className="emoji-btn" onClick={() => setShowPicker(prev => !prev)}>
          😊
        </button>
        <input
          type="text"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            handleTyping();
          }}
          placeholder="Type your message..."
        />
        <button type="submit" className="send-btn">Send</button>
      </form>
    </div>
  );
}

export default Chat;
